import { Link, useLocation, useParams } from "react-router-dom";
import cx from "classnames";

import { Car } from "@mywheels/common/lib/cars/types";
import { useTranslation } from "@mywheels/common/lib/translation";
import {
  GraphCMSQueryDef,
  useGraphCMS,
} from "@mywheels/common/lib/useGraphCMS";

import { ArrowDownIcon } from "web/ui/icons/arrows";
import { useRoutes } from "web/lib/i18n";
import { useBasicLightBox } from "web/ui/lightbox";
import { Markdown, Spinner } from "web/ui";
import { GraphCmsManualItem, GraphCmsManualStep } from "./types";

const GetItemStepsQuery: GraphCMSQueryDef<
  {
    manualItem: {
      id: string;
      manualSteps: GraphCmsManualStep[];
    };
  },
  { itemId: string; locale: string }
> = {
  query: `
    query GetItemStepsQuery($itemId: ID!, $locale: Locale!) {
      manualItem(where: { id: $itemId }, locales: [$locale]) {
        id
        manualSteps {
          id
          photo(locales:[nl]) {
            id
            thumb: url(transformation: {
              image: {
                resize: {
                  width: 200,
                  height: 100,
                  fit: clip
                }
              }
            })
            large: url(transformation: {
              image: {
                resize: {
                  width: 2000,
                  height: 1000,
                  fit: clip
                }
              }
            })
          }
          content
        }
      }
    }
  `,
};

type Props = {
  title: string;
  car: Car;
  manualSectionSlug: string;
  item: GraphCmsManualItem;
};

export function ExpandableContent({
  title,
  car,
  manualSectionSlug,
  item,
}: Props) {
  const location = useLocation();
  const routes = useRoutes();
  // eslint-disable-next-line
  const { itemId } = useParams<any>();

  const active = itemId === item.id;

  return (
    <>
      <Link
        className={cx(
          "px-3 w-full text-lg font-display hover:text-blue focus:outline-none flex items-center mb-2 py-2 text-left md:rounded-md md:focus:ring-2 focus:ring-blue",
          active ? "text-blue bg-gray100" : "text-gray800 hover:bg-gray50"
        )}
        to={
          routes.CarManualSection.url({
            carId: car.id,
            sectionId: manualSectionSlug,
            itemId: active ? undefined : item.id,
          }) + location.search
        }
      >
        <h3 className="text-xl md:text-2xl">{title}</h3>
        <div className="grow"></div>
        <div
          className={cx(
            "transition-transform text-gray",
            active && "rotate-180"
          )}
        >
          <ArrowDownIcon />
        </div>
      </Link>
      {active && (
        <div>
          <div className="py-2 px-4 md:py-4 md:px-5">
            <ManualItemSteps item={item} />
          </div>
        </div>
      )}
    </>
  );
}

function ManualItemSteps({ item }: { item: GraphCmsManualItem }) {
  const { language } = useTranslation();

  const [openLightBox, lightbox] = useBasicLightBox();

  const { data } = useGraphCMS(GetItemStepsQuery, {
    itemId: item.id,
    locale: language,
  });

  const steps = data?.manualItem.manualSteps;

  return (
    <>
      {lightbox}

      {!steps ? (
        <Spinner className="mx-auto" />
      ) : (
        steps.map((step, i) => {
          return (
            <div key={i} className="mb-2 flex">
              {step.photo && (
                <button
                  className="block mt-1 mb-4 mr-3 grow-0 flex-shrink-0 w-[100px] h-[75px] md:w-[150px] md:h-[100px]"
                  style={{
                    backgroundImage: `url(${step.photo.thumb})`,
                    backgroundSize: "cover",
                    backgroundPosition: "center center",
                  }}
                  onClick={() => {
                    if (!step.photo) return;
                    openLightBox(step.photo.large);
                  }}
                />
              )}
              <Markdown
                className="grow prose"
                source={String(step.content ?? "")}
              />
            </div>
          );
        })
      )}
    </>
  );
}
